import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';
import { MIcon, ensureMaterialSymbols } from '../components/MIcon';
import { JsonLd } from '../components/JsonLd';
import { dentistNode, faqLd } from '../seo/clinic';
import { localizedPath, Lang } from '../seo/routes';

// === Designer-Redesign (redesign emergency/) + SEO-Hub-Upgrade (Plan §4.1) ===
// Layout-Quelle: redesign emergency/code.html + DESIGN.md.
// Texte über translations.emergency.v2.* (bestehend) sowie die neuen Hub-Keys
// emergency.{triage,hubCards,weekend,hubSolve,hubFaq,common} — alle neuen
// Sektionen defensiv geguarded (fehlt der Key, wird die Sektion nicht gerendert).
// Tokens: ds-* aus tailwind.config (siehe DESIGN.md).

const TEL = '603319648';
const TEL_DISPLAY = '603 31 96 48';
const WHATSAPP_URL = 'https://wa.me/34603319648';
const MAPS_URL = 'https://maps.google.com/?q=Avenida+de+Jaime+III+3+Palma+de+Mallorca';

// FILL-Variante für Material Symbols (MIcon-Kontrakt kennt nur style, kein filled-Prop).
const ICON_FILLED: React.CSSProperties = { fontVariationSettings: "'FILL' 1" };

// Ampel-Farben der Triage-Box: sofort (rot) / heute (amber) / diese Woche (neutral).
const TRIAGE_TONES: Record<string, { box: string; badge: string; icon: string }> = {
  now: {
    box: 'border-ds-emergency-red/40 bg-ds-emergency-red/5',
    badge: 'bg-ds-emergency-red text-ds-clinical-white',
    icon: 'e911_emergency',
  },
  today: {
    box: 'border-amber-400/60 bg-amber-50',
    badge: 'bg-amber-500 text-ds-clinical-white',
    icon: 'schedule',
  },
  week: {
    box: 'border-ds-outline-variant/40 bg-ds-surface-container',
    badge: 'bg-ds-secondary text-ds-clinical-white',
    icon: 'event_available',
  },
};

const EmergencyPage: React.FC = () => {
  const { translations, language } = useLanguage();
  const lang = language as Lang;
  const em = translations.emergency;
  const v2 = em?.v2;

  // Neue Hub-Keys (Kontrakt) — dürfen fehlen, dann entfällt die jeweilige Sektion.
  const common = em?.common;
  const triage = em?.triage;
  const hubCards = em?.hubCards;
  const weekend = em?.weekend;
  const hubSolve = em?.hubSolve;
  const hubFaq = em?.hubFaq;
  const authorBox = common?.authorBox;

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    ensureMaterialSymbols();
  }, []);

  if (!v2) {
    // Fallback during HMR if translation chunk did not include v2 yet.
    return <div className="pt-24 text-center text-ds-on-surface-variant">…</div>;
  }

  const faqItems: Array<{ q: string; a: string }> = Array.isArray(hubFaq?.items) ? hubFaq.items : [];

  return (
    <main className="bg-ds-surface text-ds-on-background font-ds-body">
      <Seo pageKey="emergency" />
      {/* Vollständiger Dentist-Node mit @id — NUR hier auf dem Hub. */}
      <JsonLd data={dentistNode()} />
      {faqItems.length > 0 && <JsonLd data={faqLd(faqItems)} />}

      {/* === Hero === */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-ds-primary pt-20 lg:pt-28">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-ds-primary via-ds-primary/80 to-transparent z-10" />
          <img
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
            src="/images/SeaDent Treatment.jpg"
          />
        </div>
        <div className="relative z-20 px-gutter w-full py-section-padding-mobile md:py-section-padding-desktop">
          <div className="max-w-container-max mx-auto">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-ds-emergency-red/10 border border-ds-emergency-red/30 text-ds-emergency-red px-4 py-1.5 rounded-full mb-6 animate-ds-emergency-glow">
                <MIcon name="emergency_home" className="text-[18px]" />
                {/* Präzises Wording "Notfall-Telefon 24/7" — Fallback: bestehender Badge-Text. */}
                <span className="text-[14px] font-semibold tracking-wider">
                  {v2.location?.emergencyHours ?? v2.heroBadge}
                </span>
              </div>
              <h1 className="font-ds-display text-[40px] md:text-[48px] leading-tight text-ds-clinical-white mb-6">
                {translations.emergency.title}
                <br />
                <span className="text-ds-secondary-fixed-dim">{v2.heroAccent}</span>
              </h1>
              <p className="font-ds-body text-[18px] leading-7 text-ds-clinical-white/80 mb-10 max-w-lg">
                {v2.heroIntro}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`tel:${TEL}`}
                  className="flex items-center justify-center gap-3 bg-ds-emergency-red text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[18px] hover:bg-opacity-90 active:scale-[0.98] transition-all shadow-lg"
                >
                  <MIcon name="call" />
                  {v2.callBtn}
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-ds-success-green text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[18px] hover:bg-opacity-90 active:scale-[0.98] transition-all shadow-lg"
                >
                  <MIcon name="chat" />
                  {v2.whatsappBtn}
                </a>
              </div>
              {/* Social Proof: 5.0 ★ / 470+ Rezensionen (neuer Kontrakt-Key). */}
              {common?.calloutReviews && (
                <p className="mt-6 flex items-center gap-2 text-ds-clinical-white/80 text-[14px] font-semibold">
                  <MIcon name="star" className="text-amber-400 text-[18px]" style={ICON_FILLED} />
                  {common.calloutReviews}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* === Stats === */}
      <section className="py-12 bg-ds-clinical-white border-b border-ds-outline-variant/30">
        <div className="max-w-container-max mx-auto px-gutter grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <p className="text-ds-primary font-bold text-[32px] leading-10 font-ds-display">24/7</p>
            {/* "Notfall-Telefon 24/7" statt bloßer "Erreichbarkeit" — Fallback alter Key. */}
            <p className="text-ds-on-surface-variant text-[14px] font-semibold tracking-wider uppercase">
              {v2.location?.emergencyHours ?? v2.stats.availability}
            </p>
          </div>
          <div className="text-center border-l border-ds-outline-variant/30">
            <p className="text-ds-primary font-bold text-[32px] leading-10 font-ds-display">365</p>
            <p className="text-ds-on-surface-variant text-[14px] font-semibold tracking-wider uppercase">{v2.stats.daysAYear}</p>
          </div>
          <div className="text-center border-l border-ds-outline-variant/30">
            <p className="text-ds-primary font-bold text-[32px] leading-10 font-ds-display">&lt;30m</p>
            {/* Bedeutung: Rückruf in unter 30 Min (nie Behandlungsbeginn versprechen). */}
            <p className="text-ds-on-surface-variant text-[14px] font-semibold tracking-wider uppercase">{v2.stats.responseTime}</p>
          </div>
          <div className="text-center border-l border-ds-outline-variant/30">
            <p className="text-ds-primary font-bold text-[32px] leading-10 font-ds-display">5.0</p>
            <p className="text-ds-on-surface-variant text-[14px] font-semibold tracking-wider uppercase">
              {common?.calloutReviews ?? v2.stats.rating}
            </p>
          </div>
        </div>
      </section>

      {/* === Triage: Wann sofort anrufen? (Ampel) === */}
      {triage?.levels?.length > 0 && (
        <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter bg-ds-surface-container-lowest">
          <div className="max-w-container-max mx-auto">
            <div className="max-w-xl mb-12">
              <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-4">{triage.title}</h2>
              {triage.intro && (
                <p className="text-ds-on-surface-variant text-[18px] leading-7">{triage.intro}</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {triage.levels.map((level: any, i: number) => {
                const tone = TRIAGE_TONES[level.tone] ?? TRIAGE_TONES.week;
                return (
                  <div key={i} className={`rounded-2xl border p-8 ${tone.box}`}>
                    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-[13px] font-bold tracking-wider uppercase ${tone.badge}`}>
                      <MIcon name={tone.icon} className="text-[18px]" />
                      {level.label}
                    </div>
                    <ul className="space-y-3">
                      {level.items?.map((item: string, j: number) => (
                        <li key={j} className="flex items-start gap-2 text-[15px] leading-6 text-ds-on-surface">
                          <MIcon name="arrow_right" className="text-ds-on-surface-variant flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    {level.tone === 'now' && (
                      <a
                        href={`tel:${TEL}`}
                        className="mt-6 inline-flex items-center gap-2 text-ds-emergency-red font-bold text-[15px] hover:underline"
                      >
                        <MIcon name="call" className="text-[18px]" />
                        {common?.callNow ?? `${TEL_DISPLAY}`}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* === Notfall-Karten: 5 Teaser mit keyword-tragenden Links auf die Spokes === */}
      {hubCards?.cards?.length > 0 ? (
        <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter">
          <div className="max-w-container-max mx-auto">
            <div className="max-w-xl mb-12">
              <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-4">{hubCards.title}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hubCards.cards.map((card: any, i: number) => {
                // Optionaler Anker (z. B. Kinder-Karte → FAQ-Abschnitt auf dem Trauma-Spoke).
                const href = card.routeKey
                  ? `${localizedPath(card.routeKey, lang)}${card.anchor ? `#${card.anchor}` : ''}`
                  : null;
                return (
                  <div
                    key={i}
                    className="bg-ds-clinical-white p-8 rounded-2xl shadow-ds-card border border-ds-outline-variant/20 hover:border-ds-secondary transition-colors flex flex-col"
                  >
                    <div className="w-14 h-14 bg-ds-secondary-fixed flex items-center justify-center rounded-xl mb-6 text-ds-primary">
                      <MIcon name={card.icon || 'medical_services'} className="text-[32px]" />
                    </div>
                    <h3 className="font-ds-body text-[22px] leading-8 font-semibold text-ds-primary mb-3">{card.title}</h3>
                    <p className="text-ds-on-surface-variant mb-6 text-[16px] leading-6 flex-grow">{card.teaser}</p>
                    {href && card.linkText && (
                      <Link
                        to={href}
                        className="inline-flex items-center gap-2 text-ds-secondary font-bold text-[15px] hover:text-ds-primary transition-colors"
                      >
                        {card.linkText}
                        <MIcon name="arrow_forward" className="text-[18px]" />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : (
        /* Fallback: alte 3er-Karten, solange emergency.hubCards noch nicht befüllt ist. */
        <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter">
          <div className="max-w-container-max mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
              <div className="max-w-xl">
                <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-4">{v2.common.title}</h2>
                <p className="text-ds-on-surface-variant text-[18px] leading-7">{v2.common.intro}</p>
              </div>
              <div className="hidden md:block">
                <span className="text-ds-outline text-[12px] uppercase tracking-widest font-medium">{v2.common.eyebrow}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {v2.common.cards.map((card: any, i: number) => {
                const iconNames = ['medical_services', 'medication_liquid', 'healing'];
                return (
                  <div
                    key={i}
                    className="bg-ds-clinical-white p-8 rounded-2xl shadow-ds-card border border-ds-outline-variant/20 hover:border-ds-secondary transition-colors"
                  >
                    <div className="w-14 h-14 bg-ds-secondary-fixed flex items-center justify-center rounded-xl mb-6 text-ds-primary">
                      <MIcon name={iconNames[i] || 'medical_services'} className="text-[32px]" />
                    </div>
                    <h3 className="font-ds-body text-[24px] leading-8 font-semibold text-ds-primary mb-4">{card.title}</h3>
                    <p className="text-ds-on-surface-variant mb-6 text-[16px] leading-6">{card.description}</p>
                    <ul className="space-y-3">
                      {card.checks?.map((check: string, j: number) => (
                        <li key={j} className="flex items-center gap-2 text-[14px] font-semibold text-ds-on-surface">
                          <MIcon name="check_circle" className="text-ds-success-green" style={ICON_FILLED} />
                          {check}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* === Process === */}
      <section className="py-section-padding-mobile md:py-section-padding-desktop bg-ds-primary text-ds-clinical-white overflow-hidden">
        <div className="max-w-container-max mx-auto px-gutter grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="font-ds-display text-[28px] md:text-[32px] leading-tight mb-12">{v2.process.title}</h2>
            <div className="space-y-12">
              {v2.process.steps.map((step: any, i: number) => {
                const isFirst = i === 0;
                const isLast = i === v2.process.steps.length - 1;
                return (
                  <div key={i} className="flex gap-6 relative">
                    {!isLast && (
                      <div className="absolute left-6 top-16 bottom-[-3rem] w-[2px] bg-ds-secondary-fixed/20 hidden md:block" />
                    )}
                    <div
                      className={`flex-shrink-0 w-12 h-12 ${
                        isFirst ? 'bg-ds-emergency-red' : 'bg-ds-secondary'
                      } rounded-full flex items-center justify-center font-bold text-xl z-10`}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="font-ds-body text-[24px] leading-8 font-semibold mb-2">{step.title}</h4>
                      <p className="text-ds-clinical-white/70 text-[16px] leading-6">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-ds-secondary-fixed/10 rounded-3xl blur-2xl" />
            <img
              alt=""
              aria-hidden="true"
              className="relative rounded-3xl shadow-2xl w-full aspect-[4/5] object-cover"
              src="/images/SeaDent Approach Hands Tooth.jpg"
            />
          </div>
        </div>
      </section>

      {/* === Wochenende / Feiertage / nachts === */}
      {weekend?.title && (
        <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter bg-ds-surface-container-lowest">
          <div className="max-w-container-max mx-auto">
            <div className="max-w-3xl">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-ds-secondary-fixed flex items-center justify-center rounded-xl text-ds-primary flex-shrink-0">
                  <MIcon name="event_repeat" className="text-[32px]" />
                </div>
                <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary">{weekend.title}</h2>
              </div>
              <div className="space-y-4">
                {weekend.paragraphs?.map((p: string, i: number) => (
                  <p key={i} className="text-ds-on-surface-variant text-[17px] leading-7">{p}</p>
                ))}
              </div>
              <a
                href={`tel:${TEL}`}
                className="mt-8 inline-flex items-center gap-3 bg-ds-emergency-red text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[16px] hover:bg-opacity-90 active:scale-[0.98] transition-all shadow-lg"
              >
                <MIcon name="call" />
                {common?.callNow ?? `${TEL_DISPLAY}`}
              </a>
            </div>
          </div>
        </section>
      )}

      {/* === So lösen wir das bei SeaDent (Problem → Lösung, Cross-Links) === */}
      {hubSolve?.items?.length > 0 && (
        <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter">
          <div className="max-w-container-max mx-auto">
            <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-12">{hubSolve.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {hubSolve.items.map((item: any, i: number) => (
                <div
                  key={i}
                  className="bg-ds-clinical-white p-8 rounded-2xl shadow-ds-card border border-ds-outline-variant/20 flex flex-col"
                >
                  <p className="font-ds-body text-[18px] font-semibold text-ds-primary mb-2">{item.problem}</p>
                  <MIcon name="south" className="text-ds-secondary mb-2" />
                  <p className="text-ds-on-surface-variant text-[16px] leading-6 mb-6 flex-grow">{item.solution}</p>
                  {item.routeKey && item.linkText && (
                    <Link
                      to={localizedPath(item.routeKey, lang)}
                      className="inline-flex items-center gap-2 text-ds-secondary font-bold text-[15px] hover:text-ds-primary transition-colors"
                    >
                      {item.linkText}
                      <MIcon name="arrow_forward" className="text-[18px]" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* === Location === */}
      <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter">
        <div className="max-w-container-max mx-auto bg-ds-clinical-white rounded-3xl shadow-ds-card-lg overflow-hidden flex flex-col md:flex-row">
          <div className="md:w-1/2 p-8 md:p-16 flex flex-col justify-center">
            <span className="text-ds-secondary text-[14px] font-semibold uppercase tracking-widest mb-4">{v2.location.eyebrow}</span>
            <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-6">{v2.location.title}</h2>
            <p className="text-ds-on-surface-variant text-[18px] leading-7 mb-8">{v2.location.intro}</p>
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <MIcon name="location_on" className="text-ds-secondary" />
                <div>
                  <p className="font-bold text-ds-primary">{v2.location.addressLine1}</p>
                  <p className="text-ds-on-surface-variant">{v2.location.addressLine2}</p>
                  {/* Parkplätze Vía Roma / El Corte Inglés (neuer Kontrakt-Key). */}
                  {common?.locationLine && (
                    <p className="text-ds-on-surface-variant mt-1 text-[15px]">{common.locationLine}</p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MIcon name="schedule" className="text-ds-secondary" />
                <div>
                  <p className="font-bold text-ds-primary">{v2.location.hoursLabel}</p>
                  <p className="text-ds-on-surface-variant">{v2.location.emergencyHours}</p>
                </div>
              </div>
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-ds-primary text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[14px] tracking-wider uppercase hover:bg-ds-secondary active:scale-[0.98] transition-all self-start"
            >
              <MIcon name="directions" />
              {v2.location.directions}
            </a>
          </div>
          <div className="md:w-1/2 h-[400px] md:h-auto bg-ds-surface-container">
            <iframe
              title="SeaDent Mallorca Map"
              src="https://www.google.com/maps?q=Avenida%20de%20Jaime%20III%203%20Palma%20de%20Mallorca&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* === FAQ-Accordion (FAQPage-Schema oben via faqLd) === */}
      {faqItems.length > 0 && (
        <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter bg-ds-surface-container-lowest">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-12 text-center">{hubFaq.title}</h2>
            <div className="space-y-4">
              {faqItems.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} className="bg-ds-clinical-white rounded-xl overflow-hidden border border-ds-outline-variant/30">
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-ds-surface-container transition-colors"
                      aria-expanded={isOpen}
                    >
                      <span className="text-ds-primary font-semibold text-[17px] pr-4">{item.q}</span>
                      <MIcon
                        name="expand_more"
                        className={`text-ds-secondary flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5 text-ds-on-surface-variant leading-relaxed">{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* === Autorenbox (E-E-A-T) === */}
      {authorBox?.name && (
        <section className="px-gutter pb-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-4 bg-ds-surface-container rounded-2xl border border-ds-outline-variant/30 p-6">
              <div className="w-12 h-12 bg-ds-secondary-fixed flex items-center justify-center rounded-full text-ds-primary flex-shrink-0">
                <MIcon name="verified" style={ICON_FILLED} />
              </div>
              <div className="text-[15px] leading-6">
                <p className="text-ds-on-surface-variant">{authorBox.label}</p>
                <p className="font-bold text-ds-primary">
                  {authorBox.name}
                  {authorBox.credential ? ` — ${authorBox.credential}` : ''}
                </p>
                {authorBox.reviewedLabel && (
                  <p className="text-ds-on-surface-variant">
                    {authorBox.reviewedLabel} {authorBox.reviewedDate}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* === Final CTA === */}
      <section className="py-section-padding-mobile md:py-section-padding-desktop bg-ds-surface-container-highest">
        <div className="max-w-3xl mx-auto px-gutter text-center">
          <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-6">{v2.finalCta.title}</h2>
          <p className="text-ds-on-surface-variant text-[18px] leading-7 mb-12">{v2.finalCta.intro}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href={`tel:${TEL}`}
              className="bg-ds-emergency-red text-ds-clinical-white px-10 py-5 rounded-2xl font-bold text-[20px] shadow-xl hover:scale-[1.03] active:scale-[0.98] transition-all"
            >
              {v2.finalCta.button}
            </a>
          </div>
          <p className="mt-6 text-ds-on-surface-variant text-[14px]">
            <a href={`tel:${TEL}`} className="underline hover:text-ds-primary">{TEL_DISPLAY}</a>
          </p>
        </div>
      </section>
    </main>
  );
};

export default EmergencyPage;
